import type { Post } from '../blogTypes'

export const shedVsGable: Post = {
  slug: 'shed-vs-gable-dormer',
  title: 'Shed vs Gable Dormer: Which Is Right for Your Roof?',
  metaTitle: 'Shed vs Gable Dormer: Cost, Space and Style Compared',
  description:
    'Shed vs gable dormer compared on cost, headroom, appearance and complexity. Find out which dormer type suits your home and budget.',
  excerpt:
    'Shed dormers give you the most room for the money. Gable dormers give you the most curb appeal. Here is how to choose.',
  category: 'design-types',
  image: 'https://images.unsplash.com/photo-1787504543471-c95423873995?auto=format&fit=crop&w=1200&q=75',
  imageAlt: 'A dark tiled roof with multiple dormer windows against a clear blue sky',
  datePublished: '2025-02-10',
  dateModified: '2025-03-15',
  readMinutes: 6,
  blocks: [
    {
      type: 'p',
      text: 'Shed and gable dormers are the two most common choices, and they solve different problems. A shed dormer is a box with a single sloping roof that adds headroom and floor area. A gable dormer has a small pitched roof with a triangular front, which adds charm and light but less usable volume.',
    },
    { type: 'h2', text: 'What is a shed dormer?' },
    {
      type: 'p',
      text: 'A shed dormer has one flat sloping roof plane, set at a shallower pitch than the main roof. It can run along most of the roof width, which makes it ideal for converting a cramped attic into a bedroom or bathroom. Because the roof framing is simple, labor time is lower.',
    },
    { type: 'h2', text: 'What is a gable dormer?' },
    {
      type: 'p',
      text: 'A gable dormer, also called a doghouse dormer, has two sloping roof faces that meet at a ridge, finished with a triangular gable above the window. It echoes the lines of traditional houses and works well on front elevations where appearance matters.',
    },
    { type: 'h2', text: 'Side-by-side comparison' },
    {
      type: 'ul',
      items: [
        '**Cost:** Shed dormers are usually 10 to 25 percent cheaper per square foot because the roof is simpler.',
        '**Usable space:** Shed dormers win. They add near-full ceiling height across their width.',
        '**Appearance:** Gable dormers look more traditional and blend into period homes. Shed dormers look more modern and are often kept to the rear.',
        '**Complexity:** Gable dormers need valleys, flashing and ridge tie-ins that raise labor and leak risk.',
        '**Light:** Both bring in light. A wide shed dormer can hold several windows.',
        '**Planning:** Front gable dormers are more often approved than front shed dormers, which can look boxy.',
      ],
    },
    { type: 'h2', text: 'Which should you choose?' },
    {
      type: 'p',
      text: 'Pick a **shed dormer** if you need a real room, the dormer faces the back or side, and value matters most. Pick a **gable dormer** if the dormer faces the street, your home has a traditional style, or you only want a bright window nook. Many homes use both: a wide shed dormer at the rear and a small gable dormer at the front.',
    },
    { type: 'h2', text: 'How roof pitch changes the result' },
    {
      type: 'p',
      text: 'The pitch of the dormer roof controls headroom and water shedding. A shed dormer roof should never be shallower than about 2:12 for most roofing, and 3:12 to 4:12 is safer for shingles. Gable dormers usually copy the main roof pitch. Try different pitches in the [dormer calculator](/calculator) to see how the roof area and cost change. For the geometry behind that, read [how to calculate dormer roof area](/blog/how-to-calculate-dormer-roof-area).',
    },
    {
      type: 'cta',
      title: 'Compare both types with your dimensions',
      text: 'Run your width, depth, height and pitch through the calculator and see the cost range for your chosen finish.',
    },
    { type: 'h2', text: 'Summary' },
    {
      type: 'p',
      text: 'If you need space, build a shed dormer. If you need curb appeal, build a gable dormer. If you need both, combine them and let each do what it does best.',
    },
  ],
}
